import {
  computed,
  effect,
  inject,
  Injectable,
  Injector,
  OnDestroy,
  signal,
} from '@angular/core';
import { FirmsService } from './firms.service';
import {
  combineLatest,
  concatMap,
  finalize,
  from,
  map,
  of,
  shareReplay,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { CompanySectionService } from './company-section.service';
import { environment } from 'environments/environment';
import { toSignal } from '@angular/core/rxjs-interop';
import { CacheKeyService } from './cache-key.service';
import { CompanyApiService } from './api/firms-api.service';
import { CompanySectionApiService } from './api/company-section-api.service';
import { FileService } from './file.service';
import { WareHousesService } from './ware-houses.service';
import { ErrorTypesService } from './error-types.service';
import { StandartsService } from './standarts.service';
import { UsersService } from './users.service';
import { MissionsService } from './missions.service';
import { WarehouseReportService } from './warehouse-report.service';
import { WarehouseApiService } from './api/warehouse-api.service';
import { UsersApiService } from './api/users-api.service';
import { ErrorLevelApiService } from './api/error-level-api.service';
import { ErrorTypesApiService } from './api/error-types-api.service';
import { StandartsApiService } from './api/standarts-api.service';
import { MissionsApiService } from './api/missions-api.service';
import { WarehouseReportApiService } from './api/warehouse-report-api.service';
import { WarehouseReportMetaApiService } from './api/warehouse-report-meta-api.service';
import { appDb } from 'dexie/app.db';
import { UserService } from 'app/core/user/user.service';
import { WarehouseReportErrorService } from './warehouse-report-error.service';
import { ErrorLevelService } from './error-level.service';
import { ErrorElementsApiService } from './api/error-elements-api.service';
import { ErrorElementsService } from './error-elements.service';
import { WarehouseReportErrorApiService } from './api/warehouse-report-error-api.service';

@Injectable({
  providedIn: 'root',
})
export class SyncService implements OnDestroy {
  protected cacheKeyService = inject(CacheKeyService);

  protected fileService = inject(FileService);

  protected companyApiService = inject(CompanyApiService);
  protected companySectionApiService = inject(CompanySectionApiService);
  protected warehouseApiSerive = inject(WarehouseApiService);
  protected usersApiService = inject(UsersApiService);
  protected errorLevelApiService = inject(ErrorLevelApiService);
  protected errorTypesApiService = inject(ErrorTypesApiService);
  protected errorElementsApiService = inject(ErrorElementsApiService);
  protected standartsApiService = inject(StandartsApiService);
  protected missionsApiService = inject(MissionsApiService);
  protected warehouseReportApiService = inject(WarehouseReportApiService);
  protected warehouseReportErrorApiService = inject(
    WarehouseReportErrorApiService
  );
  protected warehouseReportMetaApiService = inject(
    WarehouseReportMetaApiService
  );

  protected userService = inject(UserService);
  protected companyService = inject(FirmsService);
  protected companySectionService = inject(CompanySectionService);
  protected warehousesService = inject(WareHousesService);
  protected errorTypesService = inject(ErrorTypesService);
  protected standartsService = inject(StandartsService);
  protected usersService = inject(UsersService);
  protected missionsService = inject(MissionsService);
  protected warehouseReportService = inject(WarehouseReportService);
  protected warehouseReportErrorService = inject(WarehouseReportErrorService);
  protected errorLevelService = inject(ErrorLevelService);
  protected errorElementsService = inject(ErrorElementsService);

  protected injector = inject(Injector);

  public isLoading = signal<boolean>(false);
  public isOnline = signal<boolean>(true);
  public isCountDownStarted = true;
  private countdownTrigger$: Subject<void> = new Subject<void>();
  private countdownStopper$: Subject<void> = new Subject<void>();
  private countdownSubscription: Subscription | null = null; // Hold the countdown subscription
  private isOnlineSubscription: Subscription | null = null; // Hold the countdown subscription
  protected currentTime = toSignal<number>(
    this.countdownTrigger$.pipe(
      concatMap(() => {
        // Ensure the current time is set before a new timer starts
        return timer(0, 1000).pipe(
          // Use timer(0, 1000) for immediate first tick
          map((val) => environment.syncPeriod - val), // Calculate remaining time
          tap((val) => {
            // Use tap for side effects like logging or handling 0
            if (val <= -1) {
              this.countdownStopper$.next(); // Stop the current timer
              // The effect will handle triggering makeHttpCalls when currentTime hits 0
            }
          }),
          takeUntil(this.countdownStopper$) // Stop this specific timer instance
        );
      })
    ),
    { initialValue: environment.syncPeriod, injector: this.injector } as any
  );
  public displayTime = computed(() => {
    const seconds = this.currentTime?.() || environment.syncPeriod;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(remainingSeconds).padStart(2, '0');
    return `${formattedMinutes}:${formattedSeconds}`;
  });
  protected services = {
    companyKey: (c) => this.companyApiService.getFirms(c),
    companySectionKey: (c) =>
      this.companySectionApiService.getCompanySections(c),
    userKey: (c) => this.usersApiService.getUsers(c),
    wareHouseKey: (c) => this.warehouseApiSerive.getWareHouseList(c),
    errorLevelKey: (c) => this.errorLevelApiService.getList(c),
    errorElementKey: (c) => this.errorElementsApiService.getList(c),
    typeErrorKey: (c) => this.errorTypesApiService.getList(c),
    standardKey: (c) => this.standartsApiService.getList(c),
    userMissionKey: (c) => this.missionsApiService.getMissions(c),
    wareHouseReportKey: (c) => this.warehouseReportApiService.getList(c),
    wareHouseReportError: (c) => this.warehouseReportErrorApiService.getList(c),
  };
  constructor() {
    effect(
      () => {
        const current = this.currentTime();
        const loading = this.isLoading();
        if (!loading) {
          if (current <= 0) {
            // Time hit 0 (or less), trigger HTTP calls
            this.startSync();
          }
        }
      },
      { injector: this.injector }
    );
    this.checkIsOnline();
  }

  ngOnDestroy(): void {
    this.stopCountdown(); // Stop any active countdown
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe(); // Unsubscribe the main stream
    }
    if (this.isOnlineSubscription) {
      this.isOnlineSubscription.unsubscribe();
    }
    this.countdownTrigger$.complete();
    this.countdownStopper$.complete();
  }

  getNotSyncedObjectsCount() {
    return combineLatest([
      this.companyService.getNotSyncedItemsCount(),
      this.companySectionService.getNotSyncedItemsCount(),
      this.warehousesService.getNotSyncedItemsCount(),
      this.warehouseReportService.getNotSyncedItemsCount(),
      this.warehouseReportErrorService.getNotSyncedItemsCount(),
      this.errorTypesService.getNotSyncedItemsCount(),
      this.errorLevelService.getNotSyncedItemsCount(),
      this.errorElementsService.getNotSyncedItemsCount(),
      this.standartsService.getNotSyncedItemsCount(),
      this.usersService.getNotSyncedItemsCount(),
      this.missionsService.getNotSyncedItemsCount(),
    ]).pipe(
      map((counts) => counts.reduce((acc, count) => (acc += count), 0)),
      shareReplay(1)
    );
  }

  checkIsOnline() {
    this.isOnlineSubscription = timer(0, 1000)
      .pipe(
        tap(() => {
          this.isOnline.set(navigator.onLine);
          if (!this.isCountDownStarted) {
            this.startCountdown();
          }
          if (!navigator.onLine) {
            this.stopCountdown();
          }
        })
      )
      .subscribe();
  }

  startSync() {
    if (this.isLoading()) {
      return;
    }

    this.isLoading.set(true);

    const cacheUpdateFcns = {
      companyKey: (hash) => this.companyApiService.getFirms(hash),
      companySectionKey: (hash) =>
        this.companySectionApiService.getCompanySections(hash),
    };

    this.cacheKeyService
      .checkAndUpdateTables(undefined, cacheUpdateFcns)
      .pipe(
        switchMap(() => {
          return of(null).pipe(
            concatMap(() => this.fileService.getNotSyncedFiles()),
            concatMap((files) =>
              !files.length
                ? of([])
                : this.fileService
                    .bulkUploadFiles(files.map((item) => item.file))
                    .pipe(
                      switchMap((res: any) => {
                        const uploadedList = (res.items || []).map(
                          (item, i) => {
                            return {
                              ...files[i],
                              url: item.dto,
                              errorMessage: item.errorMessage,
                            };
                          }
                        );
                        return !uploadedList.length
                          ? of(null)
                          : combineLatest([
                              this.fileService.bulkDeleteFiles(
                                uploadedList
                                  .map((item) =>
                                    !item.errorMessage ? item.hash : null
                                  )
                                  .filter(Boolean)
                              ),
                              this.replaceFilesOnBulk(uploadedList),
                            ]);
                      })
                    )
            ),
            concatMap(() => this.companyService.getNotSyncedCompanies()),
            concatMap((companies) => this.companyBulkPush(companies)),
            concatMap((companiesResponse) =>
              this.companySectionService
                .getNotSyncedCompanySections()
                .pipe(
                  map((sections) =>
                    sections.filter(
                      (section) =>
                        !companiesResponse.length ||
                        companiesResponse.some(
                          (res) =>
                            (!res.errorMessage &&
                              res.dto.id === section.companyId) ||
                            res.dto.id !== section.companyId
                        )
                    )
                  )
                )
            ),
            concatMap((sections) => this.sectionBulkPush(sections)),
            concatMap((sectionsResponse) =>
              this.warehousesService
                .getNotSyncedWarehouses()
                .pipe(
                  map((warehouses) =>
                    warehouses.filter(
                      (warehouse) =>
                        !sectionsResponse.length ||
                        sectionsResponse.some(
                          (res) =>
                            (!res.errorMessage &&
                              res.dto.id === warehouse.companySectionId) ||
                            res.dto.id !== warehouse.companySectionId
                        )
                    )
                  )
                )
            ),
            concatMap((warehouses) => this.warehouseBulkPush(warehouses)),
            concatMap(() => this.warehouseReportBulkPush()),
            concatMap(() =>
              combineLatest([
                this.errorTypesBulkPush(),
                this.errorLevelsBulkPush(),
                this.errorElementsBulkPush(),
              ])
            ),
            concatMap(() => this.warehouseReportErrorBulkPush()),
            concatMap(() => this.usersBulkPush()),
            concatMap(() => this.missionsBulkPush()),
            concatMap(() =>
              this.userService.get().pipe(
                switchMap((user) => {
                  if (user?.role === 'User') {
                    this.services.userKey = (c) => from(appDb.user.put(user));
                  }
                  return this.cacheKeyService
                    .checkAndUpdateTables(undefined, this.services)
                    .pipe(
                      switchMap((res) =>
                        res.length
                          ? combineLatest([
                              ...res.map(
                                (item) =>
                                  this.services[item.id]?.(
                                    item.oldHash || ''
                                  ) || of(null)
                              ),
                            ]).pipe(map(() => true))
                          : of(true)
                      )
                    );
                })
              )
            )
          );
        }),
        tap(() => this.resetTimer())
      )
      .subscribe({
        next: (result) => {
          console.log('Last call completed in sequence:', result);
        },
        error: (err) => {
          this.resetTimer();
          console.error(
            'An unhandled error occurred during sequential calls:',
            err
          );
        },
      });
  }

  public startCountdown(): void {
    // Stop any previously running timer by emitting to stopper
    this.countdownStopper$.next();
    // Then trigger a new timer by emitting to trigger
    this.countdownTrigger$.next();
    this.isCountDownStarted = true;
  }

  private normalizePushData(items) {
    return items.map((item) => {
      const d = {
        ...item,
        isDeleted: item.isDeleted ? true : false,
      };
      delete d.isSynced;
      if ('errorMessage' in d) {
        delete d.errorMessage;
      }
      return d;
    });
  }

  private companyBulkPush(companies) {
    companies = this.normalizePushData(companies);
    return !companies.length
      ? of([])
      : this.companyApiService.bulk(companies).pipe(
          switchMap((res: any) => {
            return combineLatest([
              ...res.items.map((item) =>
                this.companyService.localUpdate(
                  item.dto.id,
                  {
                    ...item.dto,
                    isDeleted: item.isDeleted ? 1 : 0,
                    errorMessage: item.errorMessage,
                  },
                  true
                )
              ),
            ]).pipe(map(() => res.items));
          })
        );
  }

  private sectionBulkPush(sections) {
    sections = this.normalizePushData(sections);
    return !sections.length
      ? of([])
      : this.companySectionApiService.bulk(sections).pipe(
          switchMap((res: any) => {
            return combineLatest([
              ...res.items.map((item) =>
                this.companySectionService.localUpdate(
                  item.dto.id,
                  {
                    ...item.dto,
                    isDeleted: item.isDeleted ? 1 : 0,
                    errorMessage: item.errorMessage,
                  },
                  true
                )
              ),
            ]).pipe(map(() => res.items));
          })
        );
  }

  private warehouseBulkPush(warehouses) {
    warehouses = this.normalizePushData(warehouses);
    return !warehouses.length
      ? of([])
      : this.warehouseApiSerive.bulk(warehouses).pipe(
          switchMap((res: any) => {
            return combineLatest([
              ...res.items.map((item) =>
                this.warehousesService.localUpdate(
                  item.dto.id,
                  {
                    ...item.dto,
                    isDeleted: item.isDeleted ? 1 : 0,
                    errorMessage: item.errorMessage,
                  },
                  true
                )
              ),
            ]).pipe(map(() => res.items));
          })
        );
  }

  private warehouseReportBulkPush() {
    return this.warehouseReportService.getNotSyncedReports().pipe(
      switchMap((reports) => {
        return !reports.length
          ? of(true)
          : this.warehouseReportApiService.bulk(reports).pipe(
              switchMap((res: any) => {
                return this.warehouseReportService.bulkUpdateLocal(
                  res.items.map((item) => ({
                    id: item.dto.id,
                    errorMessage: item.errorMessage || '',
                  }))
                );
              })
            );
      })
    );
  }

  private warehouseReportErrorBulkPush() {
    return this.warehouseReportErrorService.getNotSyncedItems().pipe(
      switchMap((reportErrors) => {
        return !reportErrors.length
          ? of(true)
          : this.warehouseReportErrorApiService.bulk(reportErrors).pipe(
              switchMap((res: any) => {
                return this.warehouseReportErrorService.bulkUpdateLocal(
                  res.items.map((item) => ({
                    id: item.dto.id,
                    errorMessage: item.errorMessage || '',
                  }))
                );
              })
            );
      })
    );
  }

  private errorTypesBulkPush() {
    return this.errorTypesService.getNotSyncedItems().pipe(
      switchMap((typeErrors) => {
        return !typeErrors.length
          ? of(true)
          : this.errorTypesApiService.bulk(typeErrors).pipe(
              switchMap((res: any) => {
                return this.errorTypesService.bulkUpdateLocal(
                  res.items.map((item) => ({
                    id: item.dto.id,
                    errorMessage: item.errorMessage || '',
                  }))
                );
              })
            );
      })
    );
  }

  private errorLevelsBulkPush() {
    return this.errorLevelService.getNotSyncedItems().pipe(
      switchMap((errorLevels) => {
        return !errorLevels.length
          ? of(true)
          : this.errorLevelApiService.bulk(errorLevels).pipe(
              switchMap((res: any) => {
                return this.errorLevelService.bulkUpdateLocal(
                  res.items.map((item) => ({
                    id: item.dto.id,
                    errorMessage: item.errorMessage || '',
                  }))
                );
              })
            );
      })
    );
  }

  private errorElementsBulkPush() {
    return this.errorElementsService.getNotSyncedItems().pipe(
      switchMap((errorElements) => {
        return !errorElements.length
          ? of(true)
          : this.errorElementsApiService.bulk(errorElements).pipe(
              switchMap((res: any) => {
                return this.errorLevelService.bulkUpdateLocal(
                  res.items.map((item) => ({
                    id: item.dto.id,
                    errorMessage: item.errorMessage || '',
                  }))
                );
              })
            );
      })
    );
  }

  private usersBulkPush() {
    return this.usersService.getNotSyncedItems().pipe(
      switchMap((users) => {
        return !users.length
          ? of(true)
          : this.usersApiService.bulk(users).pipe(
              switchMap((res: any) => {
                return this.usersService.bulkUpdateLocal(
                  res.items.map((item) => ({
                    id: item.dto.id,
                    errorMessage: item.errorMessage || '',
                  }))
                );
              })
            );
      })
    );
  }

  private missionsBulkPush() {
    return this.missionsService.getNotSyncedItems().pipe(
      switchMap((missions) => {
        return !missions.length
          ? of(true)
          : this.missionsApiService.bulk(missions).pipe(
              switchMap((res: any) => {
                return this.missionsService.bulkUpdateLocal(
                  res.items.map((item) => ({
                    id: item.dto.id,
                    errorMessage: item.errorMessage || '',
                  }))
                );
              })
            );
      })
    );
  }

  private stopCountdown(): void {
    this.countdownStopper$.next();
    this.isCountDownStarted = false;
  }

  private resetTimer(): void {
    this.stopCountdown(); // Stop current timer
    this.startCountdown(); // Start a new timer
    timer(1).subscribe(() => {
      this.isLoading.set(false); // Ensure loading is off
    });
  }

  private replaceFilesOnBulk(items) {
    const alreadyChanged = {};
    return combineLatest(
      items.map((item) => {
        if (item.relation.type === 'company') {
          return this.companyService.localUpdate(item.relation.id, {
            [item.relation.replaceWith || 'logo']: item.url,
          });
        } else if (item.relation.type === 'standart') {
          return this.standartsService.localUpdate(item.relation.id, {
            [item.relation.replaceWith || 'logo']: item.url,
          });
        } else if (item.relation.type === 'warehouseReportError') {
          return (
            alreadyChanged[item.relation.id]
              ? of(alreadyChanged[item.relation.id])
              : this.warehouseReportErrorService.get(item.relation.id)
          ).pipe(
            switchMap((reportError) => {
              alreadyChanged[item.relation.id] =
                alreadyChanged[item.relation.id] || reportError;

              const images = (alreadyChanged[item.relation.id].images ||
                []) as Array<string>;
              const imageIndex = images.findIndex((image) =>
                image.includes(item.hash)
              );
              if (imageIndex > -1) {
                images[imageIndex] = item.url;
                alreadyChanged[item.relation.id].images = images;
              }
              return this.warehouseReportErrorService.localUpdate(
                item.relation.id,
                {
                  images,
                }
              );
            })
          );
        } else {
          return of(null);
        }
      })
    );
  }
}
